import { useEffect, useState, useMemo } from "react";
import useFetchData from "../../common/useFetchData.js";

import { arrayOf, bool, oneOf, shape, string } from "prop-types";
import { propTypes as storyPromoFields } from "../StoryPromo/StoryPromo.jsx";
import queryString from "query-string";
import uniqueId from "lodash.uniqueid";

import Button from "../Button/Button.jsx";
import Container255025 from "./Container255025.jsx";
import Container5050 from "../../../../Common/js/components/Container/Container5050.jsx";
import ContainerFull from "../../../../Common/js/components/Container/ContainerFull.jsx";
import StoryPromo from "../StoryPromo/StoryPromo.jsx";

const storyMultiPromoRowShape = shape({
  alignment: oneOf(["left", "right", "middle"]),
  stories: arrayOf(shape(storyPromoFields)), // should be 3
});

/**
 * dark theme example, wrap in:
 *     <ContainerHasBackground style={{
 *       backgroundColor: "#0c4456",
 *       "--c-story-promo__title": "white",
 *       "--c-story-promo__description": "white",
 *     }} ...>
 *       <StoryMultiPromo ... />
 *     </ContainerHasBackground>
 */

const propTypes = {
  title: string,
  rows: arrayOf(storyMultiPromoRowShape),
  loadMoreEndpoint: string, // loadMoreEndpoint to load the next page
  hasMoreData: bool,
  showLoadMoreButton: bool,
};

const defaultProps = {
  rows: [],
};

const StoryMultiPromo = (props) => {
  const {
    loadMoreEndpoint,
    title,
    rows,
    hasMoreData,
    showLoadMoreButton,
  } = props;

  // the first couple pages are provided in props
  const initialPage = rows.length;
  const sessionId = useMemo(() => uniqueId("story-listing-loaded-rows-"), []);

  const [page, setPage] = useState(rows.length);
  const [loadedRows, setLoadedRows] = useState([]);

  const [moreDataAvailable, setMoreDataAvailable] = useState(hasMoreData);

  // only make a fetch call if the page is after the pages we already rendered
  // ie, if the user clicked load more
  const url =
    page > initialPage
      ? queryString.stringifyUrl({
          url: loadMoreEndpoint,
          query: {
            page,
            alignment:
              page % 3 === 1 ? "left" : page % 3 === 2 ? "right" : "middle",
          },
        })
      : null;

  const { isLoading, error, data } = useFetchData(url);

  useEffect(() => {
    const sessionItem = window.sessionStorage.getItem(sessionId);
    if (sessionItem) setLoadedRows(JSON.parse(sessionItem));
  }, [sessionId]);

  useEffect(() => {
    window.sessionStorage.setItem(sessionId, JSON.stringify(loadedRows));
  }, [loadedRows, sessionId]);

  useEffect(() => {
    // after new data is loaded, append it to the loaded row
    if (data?.rows && data.rows[0].stories.length) {
      setMoreDataAvailable(data.hasMoreData);
      setLoadedRows((existingRows) => [...existingRows, ...data.rows]);
    } else {
      setMoreDataAvailable(false);
    }
  }, [data]);

  const renderRow = (row, i) => {
    const { alignment, stories = [] } = row;

    // alignment middle
    if (alignment === "middle") {
      return (
        <Container255025
          key={i + alignment}
          left={stories[0] && <StoryPromo {...stories[0]} small />}
          middle={stories[1] && <StoryPromo {...stories[1]} />}
          right={stories[2] && <StoryPromo {...stories[2]} small />}
        />
      );
    }
    // alignment right
    else if (alignment === "right") {
      return (
        <Container5050
          key={i + alignment}
          left={
            <Container5050
              left={stories[0] && <StoryPromo {...stories[0]} small />}
              right={stories[1] && <StoryPromo {...stories[1]} small />}
            />
          }
          right={stories[2] && <StoryPromo {...stories[2]} small={false} />}
        />
      );
    }
    // alignment left
    else {
      return (
        <Container5050
          key={i + alignment}
          left={stories[0] && <StoryPromo {...stories[0]} small={false} />}
          right={
            <Container5050
              left={stories[1] && <StoryPromo {...stories[1]} small />}
              right={stories[2] && <StoryPromo {...stories[2]} small />}
            />
          }
        />
      );
    }
  };

  function handleScroll() {
    if (
      window.innerHeight + document.documentElement.scrollTop + 1 <
        document.documentElement.offsetHeight ||
      !loadMoreEndpoint ||
      !moreDataAvailable
    ) {
      return;
    }

    setPage((page) => page + 1);
  }

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <ContainerFull className="story-multi-promo">
      {title && <h2 className="story-multi-promo__title">{title}</h2>}
      <div className="story-multi-promo__stories" aria-live="polite">
        {rows.map(renderRow)}

        {loadedRows.map(renderRow)}

        <div className="story-multi-promo__load-more">
          {moreDataAvailable ? (
            isLoading ? (
              // inline the Exaptive loading spinner
              <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                version="1.1"
                id="Layer_1"
                x="0px"
                y="0px"
                viewBox="0 0 38 38"
                className="story-multi-promo__loading-spinner"
                xmlSpace="preserve"
              >
                <g>
                  <linearGradient
                    id="Oval-2_1_"
                    gradientUnits="userSpaceOnUse"
                    x1="-151.047"
                    y1="3.4499"
                    x2="-150.5746"
                    y2="3.6455"
                    gradientTransform="matrix(18 0 0 18 2739 -59)"
                  >
                    <stop
                      offset="0"
                      className="story-multi-promo__loading-spinner-stop-0"
                    />
                    <stop
                      offset="1"
                      className="story-multi-promo__loading-spinner-stop-1"
                    />
                  </linearGradient>
                  <path
                    id="Oval-2"
                    className="st0"
                    d="M33.5,18.5c0-8.1-6.6-14.8-14.8-14.8 "
                  >
                    <animateTransform
                      accumulate="none"
                      additive="replace"
                      attributeName="transform"
                      calcMode="linear"
                      dur="0.9s"
                      fill="remove"
                      from="0 18 18"
                      repeatCount="indefinite"
                      restart="always"
                      to="360 18 18"
                      type="rotate"
                    ></animateTransform>
                  </path>
                  <circle className="st1" cx="33.5" cy="18.5" r="0.8">
                    <animateTransform
                      accumulate="none"
                      additive="replace"
                      attributeName="transform"
                      calcMode="linear"
                      dur="0.9s"
                      fill="remove"
                      from="0 18 18"
                      repeatCount="indefinite"
                      restart="always"
                      to="360 18 18"
                      type="rotate"
                    ></animateTransform>
                  </circle>
                </g>
              </svg>
            ) : error ? (
              "There was an error fetching more stories, please try again later."
            ) : (
              showLoadMoreButton &&
              loadMoreEndpoint && (
                <Button
                  className="btn--primary"
                  onClick={() => {
                    setPage((page) => page + 1);
                  }}
                >
                  View More
                </Button>
              )
            )
          ) : (
            ""
          )}
        </div>
      </div>
    </ContainerFull>
  );
};

StoryMultiPromo.propTypes = propTypes;
StoryMultiPromo.defaultProps = defaultProps;
export default StoryMultiPromo;
